import { useQuery } from '@tanstack/react-query';
import type {
  DeployFailureContext,
  DeployFixInAgentResult,
} from '@volley/crucible-ipc-types/ipc-channels';
import { useCrucibleClient } from '../transport/context';
import { projectQueryKey } from '../transport/project-query-key';

/**
 * React Query hooks around the two `deploy:*` IPC channels the
 * renderer uses (#277).
 *
 *  - `useDeployFailure(gameId, enabled)` — one-shot fetch of the
 *    failure context for the latest workflow run. Disabled by
 *    default; the Workspace flips `enabled=true` only after the
 *    `publish:run` IPC has rejected (so we don't burn rate-limit
 *    budget on every workspace mount). Refetch is exposed so the
 *    Workspace can re-fetch after a fresh deploy completes.
 *
 *  - `useFixInAgent` — imperative wrapper used by the card's
 *    Fix-in-agent CTA. The IPC formats the failure context into a
 *    structured chat prompt; the card's `onFixInAgent` callback
 *    dispatches that prompt through the chat reducer.
 *
 * `deploy:retry` is intentionally NOT wired to a hook: the card's
 * "Retry deploy" button calls the parent's `onRetry` callback which
 * fires a fresh `publish:run` against the latest `main`. GitHub's
 * `rerun-failed-jobs` endpoint reruns the original SHA — useless
 * after the agent has pushed a fix. Cursor Bugbot round 1 on PR
 * #279 caught this; the IPC + helper survive in `deploy-failure.ts`
 * for completeness but the renderer doesn't call them.
 *
 * The reuse of React Query mirrors `useBugDraft.ts` for #246 — same
 * caching model, same `staleTime` shape so two surfaces of the same
 * game can share a single in-flight request without manual ref
 * juggling. Polling is intentionally NOT enabled here: the deploy
 * flow either resolves on user action or surfaces via the existing
 * `publish:progress` channel, so we don't need a background poll
 * eating the GitHub API's per-hour budget.
 */

const FAILURE_CONTEXT_OPTIONS = {
  staleTime: 30_000,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  retry: false,
} as const;

export function useDeployFailure(
  gameId: string | undefined,
  enabled: boolean,
): {
  data: DeployFailureContext | undefined;
  isLoading: boolean;
  refetch: () => void;
} {
  const client = useCrucibleClient();
  const query = useQuery<DeployFailureContext>({
    queryKey: projectQueryKey(client, ['deploy:get-failure-context', gameId]),
    queryFn: async () => {
      if (!gameId) throw new Error('gameId is required');
      return await client.invoke('deploy:get-failure-context', {
        gameId,
      });
    },
    enabled: enabled && typeof gameId === 'string' && gameId.length > 0,
    ...FAILURE_CONTEXT_OPTIONS,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    refetch: () => {
      void query.refetch();
    },
  };
}

export function useFixInAgent(): {
  fixInAgent: (args: {
    gameId: string;
    context: DeployFailureContext;
  }) => Promise<DeployFixInAgentResult>;
} {
  const client = useCrucibleClient();
  return {
    fixInAgent: async (args) => {
      return await client.invoke('deploy:fix-in-agent', args);
    },
  };
}
